import { gql, useApolloClient, useQuery } from '@apollo/client'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Title, useTranslate } from 'react-admin'
import { fileStorage, useWarnBeforeLeaving, zip } from '~/helpers'
import {
  correct,
  type Correction,
  decode,
  DEFAULTS,
  finish,
  IDENTITY,
  logoArea,
  logoBox,
  measure,
  type Position,
  type Rotation,
  scale,
  type Settings,
} from './process'

/**
 * "Bilder aufbereiten": the office's Photoshop routine as a page.
 *
 *  1. drop the photos — each is corrected automatically (tone, colour cast,
 *     dark interiors) and shown as it will come out;
 *  2. sort them by dragging, rotate or leave a photo uncorrected;
 *  3. finish: resize, sharpen and put the logo on, then download them as a
 *     ZIP or add them to a boat in this order — which is the order the NautiX
 *     feed hands to Boatvertizer and the website.
 *
 * Everything runs in the browser; nothing leaves the PC before step 3.
 */

interface Photo {
  id: string
  file: File
  name: string
  rotation: Rotation
  /** Measured once, on the preview; rotation does not change it. */
  correction: Correction
  corrected: boolean
  /** Previews: as shot, and fully corrected. The tile layers the second over
   *  the first at the chosen strength — the correction is exactly that blend. */
  before: string
  after: string
  /** Preview size, after rotation. */
  width: number
  height: number
  error?: string
}

const SETTINGS_KEY = 'photoStudio.settings'
const LOGO_KEY = 'photoStudio.logo'
const DEFAULT_LOGO = `${import.meta.env.BASE_URL}android-chrome-512x512.png`
const PREVIEW_EDGE = 640

const loadSettings = (): Settings => {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') }
  } catch {
    return DEFAULTS
  }
}

const store = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Private window or full storage: the setting just does not persist.
  }
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`could not load ${src}`))
    img.src = src
  })

const previewUrl = (c: HTMLCanvasElement) =>
  new Promise<string>((resolve) =>
    c.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : ''), 'image/jpeg', 0.85)
  )

/** Before/after previews of a file at a rotation; measures the correction
 *  unless one is given. */
async function previews(file: File, rotation: Rotation, known?: Correction) {
  const bitmap = await decode(file)
  const small = scale(bitmap, rotation, PREVIEW_EDGE)
  bitmap.close()
  const before = await previewUrl(small)
  const correction = known ?? measure(small)
  correct(small, correction, 1)
  const after = await previewUrl(small)
  return { before, after, correction, width: small.width, height: small.height }
}

async function prepare(file: File): Promise<Photo> {
  const base = { id: crypto.randomUUID(), file, name: file.name, rotation: 0 as Rotation }
  try {
    return { ...base, corrected: true, ...(await previews(file, 0)) }
  } catch {
    return {
      ...base,
      correction: IDENTITY,
      corrected: false,
      before: '',
      after: '',
      width: 4,
      height: 3,
      error: 'photos.unreadable',
    }
  }
}

const POSITIONS: Position[] = ['top-right', 'top-left', 'bottom-right', 'bottom-left', 'center']

const layer = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  display: 'block',
} as const
const SIZES = [1024, 1600, 1920, 2560, 0]

const BOATS = gql`
  query PhotoStudioBoats {
    boats(order_by: { id: desc }) {
      id
      name
      manufacturer
      model
    }
  }
`

const LAST_ORDER = gql`
  query PhotoStudioLastOrder($id: Int!) {
    attachments_aggregate(where: { relation: { _eq: "boats" }, relation_id: { _eq: $id } }) {
      aggregate {
        max {
          _order
        }
      }
    }
  }
`

const ADD_PHOTO = gql`
  mutation PhotoStudioAddPhoto($object: attachments_insert_input!) {
    insert_attachments_one(object: $object) {
      id
    }
  }
`

interface BoatOption {
  id: number
  name: string | null
  manufacturer: string | null
  model: string | null
}

const boatLabel = (b: BoatOption) =>
  [b.name, [b.manufacturer, b.model].filter(Boolean).join(' ')].filter(Boolean).join(' – ') ||
  `#${b.id}`

const slug = (text: string) =>
  text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()

export const PhotoStudio = () => {
  const translate = useTranslate()
  const apollo = useApolloClient()
  const [photos, setPhotos] = useState<Photo[]>([])
  const [settings, setSettings] = useState<Settings>(loadSettings)
  const [logoSrc, setLogoSrc] = useState<string>(() => {
    try {
      return localStorage.getItem(LOGO_KEY) ?? DEFAULT_LOGO
    } catch {
      return DEFAULT_LOGO
    }
  })
  const [logo, setLogo] = useState<HTMLImageElement | null>(null)
  const [busy, setBusy] = useState<{ label: string; done: number; total: number } | null>(null)
  const [message, setMessage] = useState<{
    severity: 'success' | 'error'
    text: string
    boatId?: number
  } | null>(null)
  const [boat, setBoat] = useState<BoatOption | null>(null)
  const [prefix, setPrefix] = useState('')
  const [dragging, setDragging] = useState<number | null>(null)
  const [dropActive, setDropActive] = useState(false)
  const [compare, setCompare] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const logoInput = useRef<HTMLInputElement>(null)
  const { data: boatData } = useQuery<{ boats: BoatOption[] }>(BOATS)

  // Photos added or changed and not yet saved to a boat or downloaded: leaving
  // the page would lose them, so it asks first.
  const [unsaved, setUnsaved] = useState(false)
  const firstPhotos = useRef(true)
  useEffect(() => {
    if (firstPhotos.current) {
      firstPhotos.current = false
      return
    }
    setUnsaved(photos.length > 0)
  }, [photos])
  useWarnBeforeLeaving(unsaved, 'photos.unsaved_leave')

  useEffect(() => store(SETTINGS_KEY, JSON.stringify(settings)), [settings])
  useEffect(() => {
    let current = true
    loadImage(logoSrc)
      .then((img) => current && setLogo(img))
      .catch(() => current && setLogo(null))
    return () => {
      current = false
    }
  }, [logoSrc])

  // Object URLs of previews are released with the photo, and on leaving.
  const photosRef = useRef(photos)
  photosRef.current = photos
  useEffect(
    () => () =>
      photosRef.current.forEach((p) => {
        URL.revokeObjectURL(p.before)
        URL.revokeObjectURL(p.after)
      }),
    []
  )

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setSettings((s) => ({ ...s, [key]: value }))

  const add = useCallback(async (files: File[]) => {
    const images = files.filter(
      (f) => f.type.startsWith('image/') || /\.(jpe?g|png|webp|heic)$/i.test(f.name)
    )
    if (images.length === 0) return
    setMessage(null)
    for (let i = 0; i < images.length; i++) {
      setBusy({ label: 'photos.preparing', done: i, total: images.length })
      const photo = await prepare(images[i])
      setPhotos((current) => [...current, photo])
    }
    setBusy(null)
  }, [])

  const remove = (id: string) =>
    setPhotos((current) => {
      const gone = current.find((p) => p.id === id)
      if (gone) {
        URL.revokeObjectURL(gone.before)
        URL.revokeObjectURL(gone.after)
      }
      return current.filter((p) => p.id !== id)
    })

  const update = (id: string, change: Partial<Photo>) =>
    setPhotos((current) => current.map((p) => (p.id === id ? { ...p, ...change } : p)))

  const rotate = async (p: Photo) => {
    const rotation = ((p.rotation + 90) % 360) as Rotation
    const next = await previews(p.file, rotation, p.correction)
    URL.revokeObjectURL(p.before)
    URL.revokeObjectURL(p.after)
    update(p.id, {
      rotation,
      before: next.before,
      after: next.after,
      width: next.width,
      height: next.height,
    })
  }

  const move = (from: number, to: number) =>
    setPhotos((current) => {
      if (to < 0 || to >= current.length || from === to) return current
      const next = [...current]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })

  const usable = photos.filter((p) => !p.error)
  const baseName = prefix.trim() ? slug(prefix) : boat ? slug(boatLabel(boat)) : 'foto'
  const fileName = (i: number) => `${baseName}-${String(i + 1).padStart(2, '0')}.jpg`

  /** Run every usable photo through the full pipeline, in order. */
  const render = async (label: string, each: (blob: Blob, index: number) => Promise<void>) => {
    setMessage(null)
    for (let i = 0; i < usable.length; i++) {
      setBusy({ label, done: i, total: usable.length })
      const p = usable[i]
      const blob = await finish(
        p.file,
        p.rotation,
        p.corrected ? p.correction : null,
        settings.watermark ? logo : null,
        settings
      )
      await each(blob, i)
    }
    setBusy(null)
  }

  const download = async () => {
    try {
      const entries: { name: string; data: Blob }[] = []
      await render('photos.processing', async (blob, i) => {
        entries.push({ name: fileName(i), data: blob })
      })
      const archive = await zip(entries)
      const url = URL.createObjectURL(archive)
      const a = document.createElement('a')
      a.href = url
      a.download = `${baseName}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      setMessage({
        severity: 'success',
        text: translate('photos.downloaded', { smart_count: entries.length }),
      })
      setUnsaved(false)
    } catch (e) {
      setBusy(null)
      setMessage({ severity: 'error', text: String((e as Error).message ?? e) })
    }
  }

  const saveToBoat = async () => {
    if (!boat) return
    try {
      const { data } = await apollo.query({
        query: LAST_ORDER,
        variables: { id: boat.id },
        fetchPolicy: 'network-only',
      })
      const start = (data?.attachments_aggregate?.aggregate?.max?._order ?? 0) + 1
      await render('photos.uploading', async (blob, i) => {
        const file = new File([blob], fileName(i), { type: 'image/jpeg' })
        // A boat's image: the public boat-photos bucket, which the feed links to.
        const stored = await fileStorage.put(file, 'boat-photos')
        // Where the logo went, so the social kit adds no second one.
        const p = usable[i]
        const area =
          settings.watermark && logo ? logoArea(p.width / p.height, logo, settings) : null
        await apollo.mutate({
          mutation: ADD_PHOTO,
          variables: {
            object: {
              relation: 'boats',
              relation_id: boat.id,
              uri: stored.key,
              content_type: 'image/jpeg',
              md5: stored.md5,
              title: file.name,
              type: 'Foto',
              _order: start + i,
              logo_box: area,
            },
          },
        })
      })
      setMessage({
        severity: 'success',
        text: translate('photos.saved', { smart_count: usable.length, boat: boatLabel(boat) }),
        boatId: boat.id,
      })
      setUnsaved(false)
    } catch (e) {
      setBusy(null)
      setMessage({ severity: 'error', text: String((e as Error).message ?? e) })
    }
  }

  const chooseLogo = (file?: File) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const src = String(reader.result)
      setLogoSrc(src)
      store(LOGO_KEY, src)
    }
    reader.readAsDataURL(file)
  }

  const resetLogo = () => {
    setLogoSrc(DEFAULT_LOGO)
    store(LOGO_KEY, null)
  }

  // Where the logo lands on a photo, as percentages of the photo.
  const overlay = (p: Photo) => {
    if (!logo || !settings.watermark) return null
    const box = logoBox(p.width, p.height, logo, settings)
    return {
      left: `${(box.x / p.width) * 100}%`,
      top: `${(box.y / p.height) * 100}%`,
      width: `${(box.w / p.width) * 100}%`,
      opacity: settings.opacity,
    }
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mt: 1 }}>
      <Title title={translate('photos.title')} />

      <Card sx={{ flex: '1 1 640px', minWidth: 0 }}>
        <CardContent>
          <Box
            data-testid='photo-drop'
            onDragOver={(e) => {
              if (dragging !== null) return
              e.preventDefault()
              setDropActive(true)
            }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(e) => {
              if (dragging !== null) return
              e.preventDefault()
              setDropActive(false)
              add(Array.from(e.dataTransfer.files))
            }}
            onClick={() => input.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dropActive ? 'primary.main' : 'divider',
              bgcolor: dropActive ? 'action.hover' : 'transparent',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <AddPhotoAlternateIcon color='primary' sx={{ fontSize: 48 }} />
            <Typography variant='h6'>{translate('photos.drop')}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {translate('photos.drop_hint')}
            </Typography>
            <input
              ref={input}
              type='file'
              accept='image/*'
              multiple
              hidden
              onChange={(e) => {
                add(Array.from(e.target.files ?? []))
                e.target.value = ''
              }}
            />
          </Box>

          {busy && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2'>
                {translate(busy.label)} {busy.done + 1} / {busy.total}
              </Typography>
              <LinearProgress variant='determinate' value={(busy.done / busy.total) * 100} />
            </Box>
          )}

          {message && (
            <Alert severity={message.severity} sx={{ mt: 2 }} onClose={() => setMessage(null)}>
              {message.text}{' '}
              {message.boatId && (
                <Link to={`/boats/${message.boatId}/show`}>{translate('photos.open_boat')}</Link>
              )}
            </Alert>
          )}

          {photos.length > 0 && (
            <>
              <Stack
                direction='row'
                sx={{ mt: 3, mb: 1, alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography variant='subtitle1'>
                  {translate('photos.sort_hint', { smart_count: usable.length })}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch checked={compare} onChange={(e) => setCompare(e.target.checked)} />
                  }
                  label={translate('photos.show_original')}
                />
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 1.5,
                }}
              >
                {photos.map((p, index) => (
                  <Card
                    key={p.id}
                    variant='outlined'
                    data-testid='photo-tile'
                    draggable={!busy}
                    onDragStart={(e) => {
                      setDragging(index)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragEnd={() => setDragging(null)}
                    onDragOver={(e) => {
                      if (dragging === null) return
                      e.preventDefault()
                    }}
                    onDrop={(e) => {
                      if (dragging === null) return
                      e.preventDefault()
                      e.stopPropagation()
                      move(dragging, index)
                      setDragging(null)
                    }}
                    sx={{ opacity: dragging === index ? 0.4 : 1, cursor: 'grab' }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        aspectRatio: '4 / 3',
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {p.error ? (
                        <Typography variant='body2' color='error' sx={{ p: 1 }}>
                          {translate(p.error)}
                        </Typography>
                      ) : (
                        // The photo at its own proportions, so the logo sits
                        // where it will on the finished file.
                        <Box
                          sx={{
                            position: 'relative',
                            aspectRatio: `${p.width} / ${p.height}`,
                            ...(p.width / p.height >= 4 / 3
                              ? { width: '100%' }
                              : { height: '100%' }),
                          }}
                        >
                          <Box
                            component='img'
                            src={p.before}
                            alt={p.name}
                            draggable={false}
                            sx={layer}
                          />
                          <Box
                            component='img'
                            src={p.after}
                            alt=''
                            draggable={false}
                            sx={{
                              ...layer,
                              opacity: compare || !p.corrected ? 0 : settings.enhance,
                            }}
                          />
                          {overlay(p) && (
                            <Box
                              component='img'
                              src={logoSrc}
                              alt=''
                              draggable={false}
                              sx={{ position: 'absolute', pointerEvents: 'none', ...overlay(p) }}
                            />
                          )}
                        </Box>
                      )}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 4,
                          top: 4,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          px: 0.75,
                          borderRadius: 1,
                          fontSize: 13,
                        }}
                      >
                        {index + 1}
                      </Box>
                    </Box>
                    <Stack direction='row' sx={{ alignItems: 'center', px: 0.5 }}>
                      <Tooltip title={translate('photos.move_left')}>
                        <span>
                          <IconButton
                            size='small'
                            disabled={index === 0}
                            onClick={() => move(index, index - 1)}
                          >
                            <ArrowBackIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={translate('photos.move_right')}>
                        <span>
                          <IconButton
                            size='small'
                            disabled={index === photos.length - 1}
                            onClick={() => move(index, index + 1)}
                          >
                            <ArrowForwardIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={translate('photos.rotate')}>
                        <IconButton size='small' disabled={!!p.error} onClick={() => rotate(p)}>
                          <RotateRightIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={translate('photos.correct')}>
                        <Checkbox
                          size='small'
                          disabled={!!p.error}
                          checked={p.corrected}
                          onChange={(e) => update(p.id, { corrected: e.target.checked })}
                        />
                      </Tooltip>
                      <Box sx={{ flex: 1 }} />
                      <Tooltip title={translate('photos.remove')}>
                        <IconButton size='small' onClick={() => remove(p.id)}>
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Card>
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ flex: '1 1 320px', maxWidth: 420 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant='h6'>{translate('photos.settings')}</Typography>

            <TextField
              select
              size='small'
              label={translate('photos.size')}
              value={settings.maxEdge ?? 0}
              onChange={(e) => set('maxEdge', Number(e.target.value) || null)}
            >
              {SIZES.map((size) => (
                <MenuItem key={size} value={size}>
                  {size ? translate('photos.size_px', { size }) : translate('photos.size_original')}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography variant='body2'>
                {translate('photos.enhance')}: {Math.round(settings.enhance * 100)} %
              </Typography>
              <Slider
                size='small'
                min={0}
                max={1}
                step={0.05}
                value={settings.enhance}
                onChange={(_, v) => set('enhance', v as number)}
              />
            </Box>

            <TextField
              select
              size='small'
              label={translate('photos.sharpen')}
              value={settings.sharpen}
              onChange={(e) => set('sharpen', Number(e.target.value))}
            >
              <MenuItem value={0}>{translate('photos.sharpen_off')}</MenuItem>
              <MenuItem value={0.35}>{translate('photos.sharpen_light')}</MenuItem>
              <MenuItem value={0.7}>{translate('photos.sharpen_strong')}</MenuItem>
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.watermark}
                  onChange={(e) => set('watermark', e.target.checked)}
                />
              }
              label={translate('photos.watermark')}
            />
            {settings.watermark && (
              <>
                <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                  <Box
                    component='img'
                    src={logoSrc}
                    alt=''
                    sx={{
                      width: 56,
                      height: 56,
                      objectFit: 'contain',
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                    }}
                  />
                  <Button size='small' onClick={() => logoInput.current?.click()}>
                    {translate('photos.logo_choose')}
                  </Button>
                  {logoSrc !== DEFAULT_LOGO && (
                    <Button size='small' onClick={resetLogo}>
                      {translate('photos.logo_default')}
                    </Button>
                  )}
                  <input
                    ref={logoInput}
                    type='file'
                    accept='image/png,image/svg+xml,image/webp'
                    hidden
                    onChange={(e) => {
                      chooseLogo(e.target.files?.[0])
                      e.target.value = ''
                    }}
                  />
                </Stack>
                <TextField
                  select
                  size='small'
                  label={translate('photos.position')}
                  value={settings.position}
                  onChange={(e) => set('position', e.target.value as Position)}
                >
                  {POSITIONS.map((p) => (
                    <MenuItem key={p} value={p}>
                      {translate(`photos.positions.${p}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <Box>
                  <Typography variant='body2'>
                    {translate('photos.logo_size')}: {Math.round(settings.logoSize * 100)} %
                  </Typography>
                  <Slider
                    size='small'
                    min={0.08}
                    max={0.4}
                    step={0.01}
                    value={settings.logoSize}
                    onChange={(_, v) => set('logoSize', v as number)}
                  />
                </Box>
                <Box>
                  <Typography variant='body2'>
                    {translate('photos.opacity')}: {Math.round(settings.opacity * 100)} %
                  </Typography>
                  <Slider
                    size='small'
                    min={0.2}
                    max={1}
                    step={0.05}
                    value={settings.opacity}
                    onChange={(_, v) => set('opacity', v as number)}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.halo}
                      onChange={(e) => set('halo', e.target.checked)}
                    />
                  }
                  label={translate('photos.halo')}
                />
              </>
            )}
            <Button size='small' onClick={() => setSettings(DEFAULTS)}>
              {translate('photos.reset')}
            </Button>

            <Typography variant='h6' sx={{ pt: 1 }}>
              {translate('photos.finish')}
            </Typography>
            <TextField
              size='small'
              label={translate('photos.file_prefix')}
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              helperText={translate('photos.file_example', { name: fileName(0) })}
            />
            <Button
              variant='contained'
              startIcon={<DownloadIcon />}
              disabled={!usable.length || !!busy}
              onClick={download}
            >
              {translate('photos.download', { smart_count: usable.length })}
            </Button>

            <Autocomplete
              size='small'
              options={boatData?.boats ?? []}
              getOptionLabel={boatLabel}
              value={boat}
              onChange={(_, value) => setBoat(value)}
              renderInput={(params) => <TextField {...params} label={translate('photos.boat')} />}
            />
            <Button
              variant='contained'
              color='secondary'
              startIcon={<CloudUploadIcon />}
              disabled={!usable.length || !boat || !!busy}
              onClick={saveToBoat}
            >
              {translate('photos.save_to_boat', { smart_count: usable.length })}
            </Button>
            <Typography variant='caption' color='text.secondary'>
              {translate('photos.save_hint')}
            </Typography>
            {unsaved && (
              <Alert severity='warning' data-testid='photos-unsaved'>
                {translate('photos.unsaved', { smart_count: usable.length })}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}

export default PhotoStudio
